/**
 * @author       grady@zumper.com (Grady Kuhnline)
 * @copyright    Copyright (c) 2018, Zumper
 * @description  setMapViewportFromClusters
 */

import { ZOOM_STREET, getFittedBounds } from '@zcode/nr-utils'
import { ON_SERVER } from '@zcode/nr/es/shared/env'
import type { Listable } from '@zcode/state-type'
import WebMercatorViewport from 'viewport-mercator-project'
import { guessMapDimensions } from '../../../utils'
import type { Dispatch, GetState } from '../../types'
import { show } from '../actions'
import { updateViewState } from './updateViewState'

const VIEWPORT_PADDING = 80

export const setMapViewportFromClusters =
  ({ clusters }: { clusters: readonly Listable[] }) =>
  (dispatch: Dispatch, getState: GetState) => {
    if (ON_SERVER) {
      return
    }

    const dimensions = guessMapDimensions({ height: null, width: null })
    const clustersSet = new Set()

    let minLat = 0
    let minLng = 0
    let maxLat = 0
    let maxLng = 0
    // iterate through next redux state Clusters List and create active clusters map
    clusters.forEach((cluster) => {
      const { lat, lng, uId } = cluster
      if (!clustersSet.has(uId)) {
        clustersSet.add(uId)

        minLat = lat ? (minLat ? Math.min(minLat, lat) : lat) : minLat
        maxLat = lat ? (maxLat ? Math.max(maxLat, lat) : lat) : maxLat
        minLng = lng ? (minLng ? Math.min(minLng, lng) : lng) : minLng
        maxLng = lng ? (maxLng ? Math.max(maxLng, lng) : lng) : maxLng
      }
    })

    // Currently, the fitBounds function of WebMercatorViewport doesn't support a sing point.
    // As a result, we need to calculate our own fitted bounds using the clusters' center, dimensions,
    // and a zoom level (which we manually picked to be ZOOM_STREET).
    // Issue filed with `viewport-mercator-project` here:
    // https://github.com/uber-common/viewport-mercator-project/issues/95
    const clusterCount = clustersSet.size
    if (clusterCount === 1) {
      const cluster = clusters[0]
      const center = { lat: cluster.lat, lng: cluster.lng }
      // @ts-expect-error center.lat (and lng) can(?) be undefined, but getFittedBounds doesn't like that
      const bounds = getFittedBounds({ center, dimensions, zoom: ZOOM_STREET })
      // @ts-expect-error dimensions.width (and height) can be null, but WebMarcatorViewport doesn't like that
      const viewport = new WebMercatorViewport(dimensions).fitBounds(
        [
          [bounds.minLng, bounds.minLat],
          [bounds.maxLng, bounds.maxLat],
        ],
        { padding: VIEWPORT_PADDING }
      )
      dispatch(updateViewState(viewport))
    } else if (clusterCount > 1) {
      const bounds = [
        [minLng, minLat],
        [maxLng, maxLat],
      ] as [[number, number], [number, number]]
      // @ts-expect-error dimensions.width (and height) can be null, but WebMarcatorViewport doesn't like that
      const viewport = new WebMercatorViewport(dimensions).fitBounds(bounds, {
        padding: VIEWPORT_PADDING,
      })

      dispatch(updateViewState(viewport))
    }

    setTimeout(() => {
      dispatch(show())
    }, 250)
  }
